import React, { useState, useEffect } from 'react';
import { Users, CalendarDays, TrendingUp, Star, Download, LogIn, LogOut, Clock, Repeat, UserPlus, Award, FolderOpen } from 'lucide-react';
import DocumentsClients from './DocumentsClients';
import { api } from '../api';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, PieChart, Pie, Cell,
} from 'recharts';

const card = { background: 'var(--white)', borderRadius: 12, border: '1px solid var(--border)', padding: '20px 24px' };
const COLORS = ['#1a56db', '#16a34a', '#f59e0b', '#dc2626', '#8b5cf6', '#06b6d4'];

function KpiCard({ val, label, color, Icon, sub, onClick, active }) {
  return (
    <div onClick={onClick} style={{
      ...card, display: 'flex', alignItems: 'center', gap: 14,
      cursor: onClick ? 'pointer' : 'default',
      border: active ? `2px solid ${color}` : '1px solid var(--border)',
      boxShadow: active ? `0 0 0 3px ${color}22` : card.boxShadow,
      transition: 'border .15s, box-shadow .15s',
    }}>
      <div style={{ width: 46, height: 46, borderRadius: 12, background: active ? color + '28' : color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={20} color={color} />
      </div>
      <div>
        <div style={{ fontSize: 26, fontWeight: 800, color, lineHeight: 1 }}>{val}</div>
        <div style={{ fontSize: 12, color: '#374151', marginTop: 3 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

function Badge({ label, color, bg, border }) {
  return <span style={{ background: bg, color, border: `1px solid ${border}`, fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10 }}>{label}</span>;
}

function ClientTable({ title, Icon, iconColor, cols, rows }) {
  return (
    <div style={card}>
      <div style={{ fontWeight: 600, marginBottom: 14, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8, color: iconColor }}>
        <Icon size={15} color={iconColor} /> {title}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr style={{ borderBottom: '1px solid var(--border)' }}>
          {cols.map(h => <th key={h} style={{ textAlign: 'left', padding: '7px 0', fontSize: 11, color: 'var(--gray)', fontWeight: 500, paddingRight: 16 }}>{h}</th>)}
        </tr></thead>
        <tbody>
          {rows.map(({ c, arriveeLabel, dateLabel, duree, statut }) => (
            <tr key={c.id} style={{ borderBottom: '1px solid #f9fafb' }}>
              <td style={{ padding: '10px 0', paddingRight: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--blue-light)', color: 'var(--blue)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>{c.initiales}</div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{c.nom}</div>
                    <div style={{ fontSize: 11, color: 'var(--gray)' }}>{c.email || '—'}</div>
                  </div>
                </div>
              </td>
              <td style={{ fontSize: 12, color: '#374151', paddingRight: 16 }}>{c.numero_chambre || '—'}</td>
              <td style={{ fontSize: 12, color: '#374151', paddingRight: 16 }}>{arriveeLabel || '—'}</td>
              <td style={{ fontSize: 12, color: '#374151', paddingRight: 16 }}>{dateLabel}</td>
              <td style={{ fontSize: 12, fontWeight: 600, color: '#1a56db', paddingRight: 16 }}>{duree ? `${duree} nuit${duree > 1 ? 's' : ''}` : '—'}</td>
              <td><Badge {...statut} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const STATUT_MAP = {
  actif:    { label: 'Présent',  bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
  reserve:  { label: 'Réservé', bg: '#eff6ff', color: '#1a56db', border: '#bfdbfe' },
  checkout: { label: 'Absent',   bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
  annule:   { label: 'Annulé',  bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
  arrivee:  { label: 'Arrivée', bg: '#eff6ff', color: '#1a56db', border: '#bfdbfe' },
  depart:   { label: 'Départ',  bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
  demain:   { label: 'Demain',  bg: '#f5f3ff', color: '#7c3aed', border: '#ddd6fe' },
};

function getEffectiveStatut(c) {
  if (c.statut === 'annule') return 'annule';
  const today = new Date().toISOString().split('T')[0];
  if (c.date_arrivee && c.date_arrivee.slice(0, 10) > today) return 'reserve';
  if (c.date_depart && c.date_depart.slice(0, 10) < today && c.statut === 'actif') return 'checkout';
  return c.statut;
}

export default function AnalyseClients() {
  const [clients, setClients]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeTab, setActiveTab]         = useState('jour');
  const [activeSection, setActiveSection] = useState(null);
  const toggleSection = (key) => setActiveSection(prev => prev === key ? null : key);

  useEffect(() => {
    api.getClients().then(setClients).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const exportCSV = () => {
    const token = sessionStorage.getItem('qbdp_token');
    fetch('https://qbdp-backend-production.up.railway.app/api/export/clients', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url;
        a.download = `clients_${new Date().toISOString().slice(0,10)}.csv`; a.click();
        URL.revokeObjectURL(url);
      });
  };

  // ── Données calculées ────────────────────────────────────────────────────────
  // Utilise l'heure locale (pas UTC) pour éviter le décalage timezone
  const localStr = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const clientDateStr = (iso) => iso ? localStr(new Date(iso)) : null;

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const todayStr = localStr(today);
  const demain   = new Date(today); demain.setDate(today.getDate() + 1);
  const demainStr = localStr(demain);

  const arriveeAujourdhui  = clients.filter(c => clientDateStr(c.date_arrivee) === todayStr);
  const departAujourdhui   = clients.filter(c => clientDateStr(c.date_depart)  === todayStr && c.statut === 'actif');
  const arriveesDemain     = clients.filter(c => clientDateStr(c.date_arrivee) === demainStr && getEffectiveStatut(c) !== 'actif');
  const presentsActuels    = clients.filter(c => getEffectiveStatut(c) === 'actif');
  const reserves           = clients.filter(c => c.statut === 'reserve');

  // Durée de séjour
  const withDuration = clients
    .filter(c => c.date_arrivee && c.date_depart)
    .map(c => {
      const d = Math.round((new Date(c.date_depart) - new Date(c.date_arrivee)) / 86400000);
      return { ...c, duree: d };
    })
    .filter(c => c.duree > 0);

  const dureeAvg = withDuration.length
    ? Math.round(withDuration.reduce((s, c) => s + c.duree, 0) / withDuration.length)
    : 0;

  // Distribution durées
  const dureeDistrib = (() => {
    const buckets = { '1 nuit': 0, '2-3 nuits': 0, '4-7 nuits': 0, '8-14 nuits': 0, '15+ nuits': 0 };
    withDuration.forEach(c => {
      if (c.duree === 1) buckets['1 nuit']++;
      else if (c.duree <= 3) buckets['2-3 nuits']++;
      else if (c.duree <= 7) buckets['4-7 nuits']++;
      else if (c.duree <= 14) buckets['8-14 nuits']++;
      else buckets['15+ nuits']++;
    });
    return Object.entries(buckets).map(([name, value]) => ({ name, value }));
  })();

  // Répartition par mois d'arrivée (12 derniers mois)
  const arriveesMensuelles = (() => {
    const months = {};
    for (let i = 11; i >= 0; i--) {
      const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - i);
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
      months[key] = 0;
    }
    clients.forEach(c => {
      if (!c.date_arrivee) return;
      const key = c.date_arrivee.slice(0,7);
      if (key in months) months[key]++;
    });
    return Object.entries(months).map(([k, v]) => ({
      mois: new Date(k+'-15').toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' }),
      arrivées: v,
    }));
  })();

  // Pic de fréquentation par jour de semaine
  const parJourSemaine = (() => {
    const jours = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    const counts = Array(7).fill(0);
    clients.forEach(c => {
      if (!c.date_arrivee) return;
      const j = new Date(c.date_arrivee).getDay();
      counts[j]++;
    });
    return jours.map((j, i) => ({ jour: j, arrivées: counts[i] }));
  })();

  // Fidélité — clients récurrents (même email)
  const parEmail = (() => {
    const m = {};
    clients.forEach(c => {
      if (!c.email) return;
      const k = c.email.toLowerCase();
      if (!m[k]) m[k] = { email: k, nom: c.nom, count: 0, lastVisit: null };
      m[k].count++;
      if (!m[k].lastVisit || c.date_arrivee > m[k].lastVisit) m[k].lastVisit = c.date_arrivee;
    });
    return Object.values(m).sort((a, b) => b.count - a.count);
  })();

  const recurrents = parEmail.filter(c => c.count > 1);
  const vip        = parEmail.filter(c => c.count >= 3);
  const nouveaux   = parEmail.filter(c => c.count === 1);
  const tauxFidelite = parEmail.length > 0 ? Math.round(recurrents.length / parEmail.length * 100) : 0;

  // Répartition par type de client
  const parType = (() => {
    const m = {};
    clients.forEach(c => { const t = c.type || 'Non défini'; m[t] = (m[t]||0)+1; });
    return Object.entries(m).map(([name, value]) => ({ name, value }));
  })();

  const tabs = [
    { id: 'jour',       label: "Aujourd'hui",  Icon: CalendarDays },
    { id: 'documents',  label: 'Documents',    Icon: FolderOpen   },
    { id: 'frequence',  label: 'Fréquentation', Icon: TrendingUp   },
    { id: 'sejours',    label: 'Séjours',       Icon: Clock        },
    { id: 'fidelite',   label: 'Fidélité',      Icon: Star         },
  ];

  if (loading) return <div style={{ padding: '28px 32px', color: 'var(--gray)', fontSize: 13 }}>Chargement...</div>;

  return (
    <div style={{ padding: '28px 32px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 600 }}>Analyse Clients</h1>
          <p style={{ fontSize: 13, color: 'var(--gray)', marginTop: 4 }}>Fréquentation, séjours et fidélité — {clients.length} clients au total</p>
        </div>
        <button onClick={exportCSV}
          style={{ background: 'white', color: '#16a34a', border: '2px solid #bbf7d0', borderRadius: 10, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Download size={14} /> Exporter CSV
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid var(--border)' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
            padding: '9px 18px', border: 'none', background: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: activeTab === t.id ? 600 : 400,
            color: activeTab === t.id ? 'var(--blue)' : 'var(--gray)',
            borderBottom: activeTab === t.id ? '2px solid var(--blue)' : '2px solid transparent',
            marginBottom: -1, display: 'flex', alignItems: 'center', gap: 6,
          }}><t.Icon size={14} />{t.label}</button>
        ))}
      </div>

      {/* ── DOCUMENTS ── */}
      {activeTab === 'documents' && (
        <div style={{ margin: '-28px -32px' }}>
          <DocumentsClients />
        </div>
      )}

      {/* ── AUJOURD'HUI ── */}
      {activeTab === 'jour' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
            <KpiCard val={presentsActuels.length}   label="Présents actuellement" color="#16a34a" Icon={Users}        onClick={() => toggleSection('presents')} active={activeSection === 'presents'} />
            <KpiCard val={arriveeAujourdhui.length} label="Arrivées aujourd'hui"  color="#1a56db" Icon={LogIn}        onClick={() => toggleSection('arrivees')} active={activeSection === 'arrivees'} />
            <KpiCard val={departAujourdhui.length}  label="Départs aujourd'hui"   color="#f59e0b" Icon={LogOut}       onClick={() => toggleSection('departs')}  active={activeSection === 'departs'}  />
            <KpiCard val={arriveesDemain.length}    label="Arrivées demain"       color="#8b5cf6" Icon={CalendarDays} onClick={() => toggleSection('demain')}   active={activeSection === 'demain'}   sub="À préparer" />
          </div>

          {activeSection && (
            <div style={{ fontSize: 12, color: '#6b7280', textAlign: 'center', marginTop: -4 }}>
              Cliquez à nouveau sur la carte pour voir tous les mouvements
            </div>
          )}

          {/* Arrivées du jour */}
          {(!activeSection || activeSection === 'arrivees') && arriveeAujourdhui.length > 0 && (
            <div style={card}>
              <div style={{ fontWeight: 600, marginBottom: 14, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                <LogIn size={15} color="#1a56db" /> Arrivées d'aujourd'hui
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Client', 'Chambre / Réf.', 'Arrivée prévue', 'Départ prévu', 'Durée', 'Statut'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '7px 0', fontSize: 11, color: 'var(--gray)', fontWeight: 500, paddingRight: 16 }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {arriveeAujourdhui.map(c => {
                    const duree = c.date_depart ? Math.round((new Date(c.date_depart) - new Date(c.date_arrivee)) / 86400000) : null;
                    return (
                      <tr key={c.id} style={{ borderBottom: '1px solid #f9fafb' }}>
                        <td style={{ padding: '10px 0', paddingRight: 16 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--blue-light)', color: 'var(--blue)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>{c.initiales}</div>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 500 }}>{c.nom}</div>
                              <div style={{ fontSize: 11, color: 'var(--gray)' }}>{c.email || '—'}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ fontSize: 12, color: '#374151', paddingRight: 16 }}>{c.numero_chambre || '—'}</td>
                        <td style={{ fontSize: 12, color: '#374151', paddingRight: 16 }}>{c.date_arrivee ? new Date(c.date_arrivee).toLocaleDateString('fr-FR') : '—'}</td>
                        <td style={{ fontSize: 12, color: '#374151', paddingRight: 16 }}>{c.date_depart ? new Date(c.date_depart).toLocaleDateString('fr-FR') : '—'}</td>
                        <td style={{ fontSize: 12, fontWeight: 600, color: '#1a56db', paddingRight: 16 }}>{duree ? `${duree} nuit${duree > 1 ? 's' : ''}` : '—'}</td>
                        <td><Badge {...STATUT_MAP.arrivee} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Présents */}
          {(!activeSection || activeSection === 'presents') && presentsActuels.length > 0 && (
            <ClientTable title="Présents actuellement" Icon={Users} iconColor="#16a34a"
              cols={['Client', 'Chambre / Réf.', 'Arrivée prévue', 'Départ prévu', 'Durée', 'Statut']}
              rows={presentsActuels.map(c => {
                const duree = c.date_arrivee && c.date_depart ? Math.round((new Date(c.date_depart) - new Date(c.date_arrivee)) / 86400000) : null;
                return { c, arriveeLabel: c.date_arrivee ? new Date(c.date_arrivee).toLocaleDateString('fr-FR') : '—', dateLabel: c.date_depart ? new Date(c.date_depart).toLocaleDateString('fr-FR') : '—', duree, statut: STATUT_MAP.actif };
              })} />
          )}

          {/* Départs du jour */}
          {(!activeSection || activeSection === 'departs') && departAujourdhui.length > 0 && (
            <ClientTable title="Départs d'aujourd'hui" Icon={LogOut} iconColor="#f59e0b"
              cols={['Client', 'Chambre / Réf.', 'Arrivée prévue', 'Départ prévu', 'Durée', 'Statut']}
              rows={departAujourdhui.map(c => {
                const duree = c.date_arrivee && c.date_depart ? Math.round((new Date(c.date_depart) - new Date(c.date_arrivee)) / 86400000) : null;
                return { c, arriveeLabel: c.date_arrivee ? new Date(c.date_arrivee).toLocaleDateString('fr-FR') : '—', dateLabel: c.date_depart ? new Date(c.date_depart).toLocaleDateString('fr-FR') : '—', duree, statut: STATUT_MAP.depart };
              })} />
          )}

          {/* À venir demain */}
          {(!activeSection || activeSection === 'demain') && arriveesDemain.length > 0 && (
            <ClientTable title="Arrivées de demain — à préparer" Icon={CalendarDays} iconColor="#7c3aed"
              cols={['Client', 'Chambre / Réf.', 'Arrivée prévue', 'Départ prévu', 'Durée', 'Statut']}
              rows={arriveesDemain.map(c => {
                const duree = c.date_arrivee && c.date_depart ? Math.round((new Date(c.date_depart) - new Date(c.date_arrivee)) / 86400000) : null;
                return { c, arriveeLabel: c.date_arrivee ? new Date(c.date_arrivee).toLocaleDateString('fr-FR') : '—', dateLabel: c.date_depart ? new Date(c.date_depart).toLocaleDateString('fr-FR') : '—', duree, statut: STATUT_MAP.demain };
              })} />
          )}

          {(
            (activeSection === 'presents' && presentsActuels.length === 0) ||
            (activeSection === 'arrivees' && arriveeAujourdhui.length === 0) ||
            (activeSection === 'departs'  && departAujourdhui.length === 0) ||
            (activeSection === 'demain'   && arriveesDemain.length === 0) ||
            (!activeSection && presentsActuels.length === 0 && arriveeAujourdhui.length === 0 && departAujourdhui.length === 0 && arriveesDemain.length === 0)
          ) && (
            <div style={{ ...card, textAlign: 'center', padding: 48, color: 'var(--gray)' }}>Aucun client dans cette catégorie</div>
          )}
        </div>
      )}

      {/* ── FRÉQUENTATION ── */}
      {activeTab === 'frequence' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
            <KpiCard val={clients.filter(c=>c.statut==='actif').length}    label="Présents ce jour"      color="#16a34a" Icon={Users} />
            <KpiCard val={clients.filter(c=>c.statut==='reserve').length}  label="Réservations à venir"  color="#1a56db" Icon={CalendarDays} />
            <KpiCard val={clients.filter(c=>c.statut==='checkout').length} label="Anciens séjours"        color="#6b7280" Icon={LogOut} />
            <KpiCard val={clients.length} label="Total clients enregistrés" color="#8b5cf6" Icon={UserPlus} />
          </div>

          <div style={card}>
            <div style={{ fontWeight: 600, marginBottom: 16 }}>Arrivées par mois — 12 derniers mois</div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={arriveesMensuelles} barSize={18}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="mois" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="arrivées" fill="#1a56db" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={card}>
              <div style={{ fontWeight: 600, marginBottom: 16 }}>Jour d'arrivée préféré</div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={parJourSemaine} barSize={22}>
                  <XAxis dataKey="jour" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="arrivées" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div style={card}>
              <div style={{ fontWeight: 600, marginBottom: 16 }}>Répartition par type de client</div>
              {parType.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--gray)', padding: 40 }}>Aucune donnée</div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={parType} cx="50%" cy="50%" outerRadius={75} dataKey="value" label={({ name, value }) => `${name} (${value})`} labelLine={false} style={{ fontSize: 10 }}>
                      {parType.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── SÉJOURS ── */}
      {activeTab === 'sejours' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
            <KpiCard val={`${dureeAvg} nuit${dureeAvg > 1 ? 's' : ''}`} label="Durée moyenne de séjour" color="#1a56db" Icon={Clock} />
            <KpiCard val={withDuration.length > 0 ? `${Math.min(...withDuration.map(c=>c.duree))} nuit${Math.min(...withDuration.map(c=>c.duree))>1?'s':''}` : '—'} label="Séjour le plus court" color="#16a34a" Icon={Clock} />
            <KpiCard val={withDuration.length > 0 ? `${Math.max(...withDuration.map(c=>c.duree))} nuits` : '—'} label="Séjour le plus long" color="#f59e0b" Icon={Clock} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={card}>
              <div style={{ fontWeight: 600, marginBottom: 16 }}>Distribution des durées de séjour</div>
              {withDuration.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--gray)', padding: 40 }}>Aucune donnée</div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={dureeDistrib} barSize={24}>
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#16a34a" radius={[4, 4, 0, 0]} label={{ position: 'top', fontSize: 11 }} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            <div style={card}>
              <div style={{ fontWeight: 600, marginBottom: 14 }}>Séjours les plus longs</div>
              {withDuration.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--gray)', padding: 40 }}>Aucune donnée</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[...withDuration].sort((a,b) => b.duree - a.duree).slice(0, 6).map(c => (
                    <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f9fafb' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--blue-light)', color: 'var(--blue)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700 }}>{c.initiales}</div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 500 }}>{c.nom}</div>
                          <div style={{ fontSize: 11, color: 'var(--gray)' }}>
                            {new Date(c.date_arrivee).toLocaleDateString('fr-FR')} → {new Date(c.date_depart).toLocaleDateString('fr-FR')}
                          </div>
                        </div>
                      </div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#1a56db' }}>{c.duree} nuits</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Périodicité — checkins par tranche horaire (si données) */}
          <div style={card}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Répartition des arrivées dans l'année</div>
            <div style={{ fontSize: 12, color: 'var(--gray)', marginBottom: 16 }}>Mois les plus chargés sur toutes les années</div>
            {(() => {
              const byMonth = Array(12).fill(0);
              clients.forEach(c => { if (c.date_arrivee) byMonth[new Date(c.date_arrivee).getMonth()]++; });
              const moisNoms = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];
              const data = moisNoms.map((m, i) => ({ mois: m, arrivées: byMonth[i] }));
              return (
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="mois" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="arrivées" stroke="#1a56db" strokeWidth={2.5} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              );
            })()}
          </div>
        </div>
      )}

      {/* ── FIDÉLITÉ ── */}
      {activeTab === 'fidelite' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
            <KpiCard val={`${tauxFidelite}%`} label="Taux de fidélité"        color="#1a56db" Icon={Repeat} sub={`${recurrents.length} clients récurrents`} />
            <KpiCard val={vip.length}          label="Clients VIP (3+ séjours)" color="#f59e0b" Icon={Award} />
            <KpiCard val={nouveaux.length}     label="Clients uniques"         color="#16a34a" Icon={UserPlus} />
            <KpiCard val={recurrents.length > 0 ? Math.round(recurrents.reduce((s,c)=>s+c.count,0)/recurrents.length*10)/10 : 0} label="Séjours moy. (récurrents)" color="#8b5cf6" Icon={Star} />
          </div>

          {/* Top clients VIP */}
          {vip.length > 0 && (
            <div style={card}>
              <div style={{ fontWeight: 600, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Award size={15} color="#f59e0b" /> Clients VIP — 3 séjours et plus
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Client', 'Email', 'Nb séjours', 'Dernier séjour'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '7px 0', fontSize: 11, color: 'var(--gray)', fontWeight: 500, paddingRight: 16 }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {vip.map((c, i) => (
                    <tr key={c.email} style={{ borderBottom: '1px solid #f9fafb' }}>
                      <td style={{ padding: '10px 0', paddingRight: 16 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 28, height: 28, borderRadius: '50%', background: i < 3 ? '#fef3c7' : 'var(--blue-light)', color: i < 3 ? '#d97706' : 'var(--blue)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>
                            {i < 3 ? '★' : c.nom?.slice(0,2).toUpperCase()}
                          </div>
                          <span style={{ fontSize: 13, fontWeight: 500 }}>{c.nom}</span>
                        </div>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--gray)', paddingRight: 16 }}>{c.email}</td>
                      <td style={{ paddingRight: 16 }}>
                        <span style={{ background: '#fef3c7', color: '#d97706', fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 10 }}>{c.count} séjours</span>
                      </td>
                      <td style={{ fontSize: 12, color: '#374151' }}>{c.lastVisit ? new Date(c.lastVisit).toLocaleDateString('fr-FR') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Clients récurrents non-VIP */}
          {recurrents.filter(c => c.count < 3).length > 0 && (
            <div style={card}>
              <div style={{ fontWeight: 600, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Repeat size={15} color="#1a56db" /> Clients récurrents — 2 séjours
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {recurrents.filter(c => c.count < 3).map(c => (
                  <div key={c.email} style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '8px 14px' }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{c.nom}</div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>{c.count} séjours · {c.lastVisit ? new Date(c.lastVisit).toLocaleDateString('fr-FR') : '—'}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {recurrents.length === 0 && (
            <div style={{ ...card, textAlign: 'center', padding: 48, color: 'var(--gray)' }}>Aucun client récurrent détecté pour l'instant</div>
          )}
        </div>
      )}
    </div>
  );
}
